import { useCallback } from 'react';
import { get, post } from '../../../../components/hooks/apiFetch';
import { AI_UTILITIES } from '../../../../constants/aiUtilities';

export const useUserNotifications = () => {
    const getUtilityInfo = useCallback(() => {
        const currentPath = window.location.pathname; // например, '/transcribe/123'

        // Найти утилиту, чей путь является началом текущего пути
        const utility = Object.values(AI_UTILITIES).find(util =>
            currentPath.startsWith(util.path)
        );

        if (utility) {
            return {
                rusUtilName: utility.name,
                href: window.location.href,
            };
        }

        // Если не найдено – fallback
        return {
            rusUtilName: 'Неизвестная утилита',
            href: window.location.href,
        };
    }, []);

    const sendNotifications = useCallback(async (userLogins, messageData) => {
        try {
            // Отправка уведомления через Notification
            const notificationFormData = new FormData();
            userLogins.forEach(login => {
                notificationFormData.append('EmailLogins', login);
            });
            notificationFormData.append('Message', messageData.message);

            await post(`/api/Notification/SendNotification`, notificationFormData);

            // Отправка email
            const emailFormData = new FormData();
            userLogins.forEach(login => {
                emailFormData.append('EmailLogins', login);
            });
            emailFormData.append('Subject', messageData.subject);
            emailFormData.append('Message', messageData.message);

            await post(`/api/Email/SendMessage`, emailFormData);
            return true;
        } catch (error) {
            console.error('Ошибка при отправке уведомлений:', error);
            return false;
        }
    }, []);

    const sendFileVersionNotifications = useCallback(async (actionType, record, currentUser, id) => {
        if (!currentUser) return false;

        const { rusUtilName, href } = getUtilityInfo();

        let subject = '';
        let message = '';

        switch (actionType) {
            case 'delete':
                subject = `Удалена версия файла в обработке #${id} ${rusUtilName}`;
                message = `${currentUser.fullName} (${currentUser.login}) удалил(а) версию файла "${record.FileName}" (v.${record.Version}) в обработке #${id}. Просмотр по ссылке: ${href}`;
                break;
            case 'update':
                subject = `Создана новая версия файла в обработке #${id} ${rusUtilName}`;
                message = `${currentUser.fullName} (${currentUser.login}) создал(а) новую версию файла "${record.FileName}" (v.${record.Version}) в обработке #${id}. Просмотр по ссылке: ${href}`;
                break;
            default:
                return false;
        }

        // Получаем членов команды с правами чтения
        try {
            const teamMembersResponse = await get(`/api/TaskFileAccess/GetTeamMembersWithReadAccess/${encodeURIComponent(id)}?baseFileId=${encodeURIComponent(record.BaseFileId)}`);
            const teamMembers = await teamMembersResponse.json();
            const userLogins = teamMembers
                .filter(member => member.login !== currentUser.login)
                .map(member => member.login);

            if (userLogins.length === 0) return true;

            return await sendNotifications(userLogins, { subject, message });
        } catch (error) {
            console.error('Ошибка при получении членов команды:', error);
        }

        return false;
    }, [getUtilityInfo, sendNotifications]);

    const sendTeamNotifications = useCallback(async (userLogins, actionType, currentUser, id) => {
        const { rusUtilName, href } = getUtilityInfo();

        let subject = '';
        let message = '';

        switch (actionType) {
            case 'permissions_add':
                subject = `Получены прав доступа к обработке #${id} ${rusUtilName}`;
                message = `${currentUser.fullName} (${currentUser.login}) добавил(а) Вас в команду обработки #${id}. Просмотр по ссылке: ${href}`;
                break;
            case 'permissions_updated':
                subject = `Изменены права доступа к обработке #${id} ${rusUtilName}`;
                message = `Ваши права доступа в обработке #${id} были обновлены пользователем: ${currentUser.fullName} (${currentUser.login}). Просмотр по ссылке: ${href}`;
                break;
            case 'access_removed':
                subject = `Удалены прав доступа к обработке #${id} ${rusUtilName}`;
                message = `Ваш доступ к обработке #${id} был удалён пользователем: ${currentUser.fullName} (${currentUser.login}).`;
                break;
            case 'creator_transferred':
                subject = `Передана роль владельца обработки #${id} ${rusUtilName}`;
                message = `${currentUser.fullName} (${currentUser.login}) передал(а) Вам роль владельца обработки #${id}. Просмотр по ссылке: ${href}`;
                break;
            default:
                return false;
        }

        if (userLogins.length === 0) return true;

        return await sendNotifications(userLogins, { subject, message });
    }, [getUtilityInfo, sendNotifications]);

    return {
        sendFileVersionNotifications,
        sendTeamNotifications,
        sendNotifications
    };
};